import React, {
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import {
    LevelData,
    ParentLevelData,
    PRLevel,
} from "../../interfaces/PedagogicalResourcesManagement";
import { PageState } from "../../interfaces/Status";
import { ErrorInfo } from "../../interfaces/Error";
import { errorStore } from "../../contexts/ErrorContext";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { getModuleById } from "../../utils/dataRetrieval";
import { LegacyLoader } from "../../components/Loader/Loader";
import Manager from "./Manager/Manager";
import usePRManager from "./Manager/usePRManager";
import ActionTray from "./ActionTray/ActionTray";
import SafeguardedLeaveLink from "./SafeguardedLeaveLink/SafeguardedLeaveLink";
import useDashboard from "../../hooks/useDashboard";
import { useData } from "../../hooks/useData";
import TabTitle from "../../components/TabTitle/TabTitle";
import { pageTitles } from "../../utils/messages";
import { useDashboardRouter } from "../DashBoard/DashboardTeacher/routing";
import { useActiveSubject } from "../../hooks/useActiveSubject";
import { Subject } from "../../interfaces/User";
import { Data } from "../../interfaces/Data";
import ModuleMapLink from "../../design-system-components/ModuleMapLink/ModuleMapLink";

import "./PedagogicalResourcesManagement.scss";

const PedagogicalResourcesManagement = () => {
    const { classroomId } = useParams<{ classroomId: string }>();
    const data = useData();
    const { setErrorInfo } = useContext(errorStore);
    const prManager = usePRManager(classroomId);
    const intl = useIntl();
    const history = useHistory();
    const { singleClassroom: getSingleClassroomUrl } = useDashboardRouter();

    const [pageState, setPageState] = useState<PageState>(PageState.Loading);
    const [className, setClassName] = useState<string>();
    const [levelData, setLevelData] = useState<LevelData>({
        level: PRLevel.Modules,
        prPool: data.modules.filter(
            (mod) => mod.visibilityStatus === "VISIBLE"
        ),
    });
    const focusRef = useRef<HTMLHeadingElement>(null);

    const dashboardError: ErrorInfo = useMemo(
        () => ({
            displayModal: true,
            modal: {
                type: "POPUP",
                content: {
                    title: intl.formatMessage(messages.errorTitle),
                    text: intl.formatMessage(messages.errorText),
                    btn: {
                        label: intl.formatMessage(messages.goHome),
                        cancel: false,
                    },
                },
                onClick: () => {
                    history.push("/");
                },
            },
        }),
        [intl, history]
    );
    const dashboardQuery = useDashboard();
    useEffect(() => {
        if (!dashboardQuery.isError) return;
        setErrorInfo(dashboardError);
    }, [dashboardQuery.isError, dashboardError, setErrorInfo]);
    const dashboard = dashboardQuery.data;

    const activeSubject = useActiveSubject();

    // Set page name
    useEffect(() => {
        if (!dashboard) return;
        if (!dashboard.classrooms || dashboard.classrooms.length === 0) {
            setErrorInfo(dashboardError);
            return;
        }
        const classroom = dashboard.classrooms.find(
            (classroom) => classroom.id === classroomId
        );
        if (!classroom) {
            setErrorInfo(dashboardError);
            return;
        }

        setClassName(classroom.name);
        setPageState(PageState.Loaded);
    }, [dashboard, classroomId, dashboardError, setErrorInfo]);

    useLayoutEffect(() => {
        if (focusRef.current) {
            focusRef.current.setAttribute("tabIndex", "-1");
            focusRef.current.focus();
            focusRef.current.removeAttribute("tabIndex");
        }
    }, [levelData]);

    if (pageState === PageState.Loading) return <LegacyLoader />;

    return (
        <>
            <TabTitle title={pageTitles.prm} />

            <div className="pedagogical-resources-management">
                <aside>
                    <div>
                        <nav>
                            <SafeguardedLeaveLink
                                className="sidebar__backlink"
                                to={getSingleClassroomUrl(classroomId)}
                                dirty={prManager.dirty}
                            >
                                <Icon
                                    path="chevron_right"
                                    className="backlink__icon"
                                    color="#4E4E4E"
                                    size={20}
                                />
                                <span>{className}</span>
                            </SafeguardedLeaveLink>
                        </nav>

                        {levelData.parentLevel &&
                            prepModuleMapLink(
                                levelData.parentLevel,
                                data,
                                activeSubject
                            )}
                    </div>
                </aside>

                <main>
                    <div className="prm__content-container">
                        <h1 ref={focusRef}>
                            <span>{className}</span>
                            <span>
                                {intl.formatMessage(
                                    levelData.level === PRLevel.Modules
                                        ? messages.prmTitleModule
                                        : messages.prmTitle
                                )}
                            </span>
                        </h1>
                        <p className="prm__description">
                            {intl.formatMessage(
                                levelData.level === PRLevel.Modules
                                    ? messages.prmDescriptionModule
                                    : levelData.level === PRLevel.Objectives
                                    ? messages.prmDescriptionObjective
                                    : messages.prmDescriptionActivity
                            )}
                        </p>

                        <div className="prm__manager-container">
                            <Manager
                                prLockStatus={prManager.currentPRLockStatus}
                                toggleItems={prManager.toggleItems}
                                levelData={levelData}
                                setLevelData={setLevelData}
                            />
                        </div>
                    </div>

                    <ActionTray {...prManager} classroomId={classroomId} />
                </main>
            </div>
        </>
    );
};

function prepModuleMapLink(
    parentLevelData: ParentLevelData,
    data: Data,
    activeSubject: Subject
) {
    const module = getModuleById(parentLevelData.moduleId, data);

    return module && module.studentPath ? (
        <ModuleMapLink
            moduleMapPath={module.studentPath}
            activeSubject={activeSubject}
            isTeacher={true}
        />
    ) : (
        <></>
    );
}

const messages = defineMessages({
    errorTitle: {
        id: "error-dashboardData-title",
        defaultMessage: "Cannot retrieve classes data",
    },
    errorText: {
        id: "error-dashboardData-text",
        defaultMessage:
            "An error occured while trying to retrieve your classes' data. Try again later.",
    },
    goHome: {
        id: "errors-statements-cannotRetrieveHistory-goToHomepageButton",
        defaultMessage: "Go to homepage",
    },
    prmTitleModule: {
        id: "prm-home-title",
        defaultMessage: "Manage the module's resources",
    },
    prmTitle: {
        id: "prm-home-titleGeneric",
        defaultMessage: "Manage the resource",
    },
    prmDescriptionModule: {
        id: "prm-home-descriptionModule",
        defaultMessage:
            "You can decide to limit access to the contents of the modules by deactivating them. You can reactivate them later. Click on a module to access its content and manage access to the objectives. ",
    },
    prmDescriptionObjective: {
        id: "prm-home-descriptionObjective",
        defaultMessage:
            "You can decide to limit access to the contents of the resource by deactivating them. You can reactivate them later.",
    },
    prmDescriptionActivity: {
        id: "prm-home-descriptionActivity",
        defaultMessage:
            "You can decide to limit access to the contents of the resource by deactivating them. You can reactivate them later.",
    },
    moduleMapTitle: {
        id: "prm-moduleMap",
        defaultMessage: "Module map",
    },
    moduleMapBtnLabel: {
        id: "prm-moduleMap",
        defaultMessage: "Module map",
    },
});

export default PedagogicalResourcesManagement;
